<template>
  <div class="lights-row">
    <div class="lights-row__pucks">
      <template v-if="orderedCodes.length === 0">
        <LightDot code="unset" />
        <span class="lights-row__unset">Lights not set</span>
      </template>
      <template v-else>
        <LightDot v-for="code in orderedCodes" :key="code" :code="code" />
      </template>

      <Button
        class="lights-row__edit"
        icon="pi pi-pencil"
        text
        rounded
        title="Edit condition lights"
        @click="openEditor"
      />
    </div>

    <Popover ref="editorPopover">
      <LightsEditor :selected="lights" @toggle="toggle" @close="closeEditor" />
    </Popover>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import LightDot from '../LightDot/LightDot.vue';
import LightsEditor from '../LightsEditor/LightsEditor.vue';

// The condition lights under the run number, with a pencil that opens the
// picker.
//
// The working set is a `v-model`, not local state seeded from an item. It was
// the latter, which meant the row quietly owned domain state that outlived the
// render and the consuming application had no way to read a clerk's edit back
// out. Bound, the application owns the lights; unbound, `defineModel` keeps a
// local value so the picker still demonstrates.
//
// Codes come in as whatever the record holds — 'R', 'red', 'Red' — and are
// normalized to the single upper-case letter here, since a lights list is a
// notoriously inconsistent field across auction feeds.
defineOptions({ name: 'LightsRow' });

// The codes on this lot, in any spelling.
const lights = defineModel({ type: Array, default: () => [] });

// Display order, which is the order they are called on the block.
const LIGHT_ORDER = ['R', 'G', 'Y', 'B', 'W'];

const normalize = (value) => (typeof value === 'string' && value.length > 0 ? value[0].toUpperCase() : null);

const orderedCodes = computed(() => {
  const present = new Set((lights.value ?? []).map(normalize));
  return LIGHT_ORDER.filter((code) => present.has(code));
});

function toggle(code) {
  const present = new Set(orderedCodes.value);
  if (present.has(code)) present.delete(code);
  else present.add(code);
  lights.value = LIGHT_ORDER.filter((each) => present.has(each));
}

const editorPopover = ref(null);
const openEditor = (event) => editorPopover.value?.toggle(event);
const closeEditor = () => editorPopover.value?.hide();
</script>

<style scoped>
.lights-row {
  position: relative;
  display: flex;
  justify-content: flex-end;
}

.lights-row__pucks {
  display: flex;
  align-items: center;
  gap: var(--p-lights-row-pucks-gap);
  padding: 0 2px;
}

.lights-row__unset {
  font-size: var(--p-lights-row-unset-font-size);
  color: var(--p-lights-row-unset-color);
}

.p-button.lights-row__edit {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 120ms;
  padding: 0;
  border-radius: var(--p-lights-row-edit-border-radius);
  color: var(--p-lights-row-edit-color);
}
</style>
